import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
} from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { actualizarAvatarUsuario, subirAvatarUsuario } from "../../services/apis";
import { authFetch } from "../../services/apiClient";
import { hasAuthSession } from "../../services/authStorage";

const buildImageUrl = (personaId) =>
	`https://recon.jcinformatics.com/image.php?id_usuario=${personaId}&t=${Date.now()}`;

export function AvatarPersona({
	persona,
	size = 26,
	allowUpload = false,
	parentDialogOpen = true,
	onAvatarUploaded,
}) {
	const [src, setSrc] = useState("");
	const [openPreview, setOpenPreview] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [imageUrl, setImageUrl] = useState(persona?.imagen_url || "");
	const fileInputRef = useRef(null);

	const nombrePersona = useMemo(() => {
		return (
			persona?.nombre_completo ||
			`${persona?.nombre ?? ""} ${persona?.apellidos ?? ""}`.trim() ||
			"Usuario"
		);
	}, [persona]);

	const iniciales = useMemo(() => {
		if (!nombrePersona) return "?";

		return nombrePersona
			.split(" ")
			.filter(Boolean)
			.slice(0, 2)
			.map((parte) => parte[0]?.toUpperCase())
			.join("");
	}, [nombrePersona]);

	const canUpload = allowUpload && !!persona?.id && hasAuthSession();
	const hasImage = !!imageUrl;

	useEffect(() => {
		setImageUrl(persona?.imagen_url || "");
	}, [persona?.id, persona?.imagen_url]);

	useEffect(() => {
		if (parentDialogOpen === false) {
			setOpenPreview(false);
		}
	}, [parentDialogOpen]);

	useEffect(() => {
		let active = true;
		let objectUrl = null;

		const cargarImagen = async () => {
			if (!imageUrl || !hasAuthSession()) {
				setSrc("");
				return;
			}

			try {
				const res = await authFetch(imageUrl, {
					headers: {
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					},
				});

				if (!res.ok) throw new Error("No se pudo cargar la imagen");

				const blob = await res.blob();
				objectUrl = URL.createObjectURL(blob);

				if (active) setSrc(objectUrl);
			} catch (err) {
				console.error("Error cargando avatar:", err);
				if (active) setSrc("");
			}
		};

		cargarImagen();

		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [imageUrl]);

	const handleClosePreview = () => {
		setOpenPreview(false);
	};

	const handleAvatarClick = () => {
		if (uploading) return;

		if (src) {
			setOpenPreview(true);
			return;
		}

		if (canUpload) {
			fileInputRef.current?.click();
		}
	};

	const openFilePicker = (event) => {
		event?.stopPropagation?.();

		if (!canUpload || uploading) return;

		fileInputRef.current?.click();
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) return;

		if (!file.type.startsWith("image/")) {
			window.alert("Selecciona una imagen valida.");
			return;
		}

		setUploading(true);

		try {
			const result = hasImage
				? await actualizarAvatarUsuario(persona.id, file)
				: await subirAvatarUsuario(persona.id, file);

			if (!result?.success) {
				window.alert(
					result?.message ||
						(hasImage
							? "No se pudo actualizar la imagen"
							: "No se pudo guardar la imagen")
				);
				return;
			}

			const updatedImageUrl = buildImageUrl(persona.id);
			setImageUrl(updatedImageUrl);

			if (typeof onAvatarUploaded === "function") {
				onAvatarUploaded(
					{
						...persona,
						imagen_url: updatedImageUrl,
					},
					result
				);
			}

			window.alert(
				hasImage ? "Avatar actualizado correctamente" : "Avatar guardado correctamente"
			);
		} catch (err) {
			console.error(
				hasImage ? "Error actualizando avatar:" : "Error subiendo avatar:",
				err
			);

			window.alert(
				hasImage ? "No se pudo actualizar la imagen" : "No se pudo subir la imagen"
			);
		} finally {
			setUploading(false);
		}
	};

	return (
		<>
			<Box sx={{ position: "relative", display: "inline-flex" }}>
				<Avatar
					src={src || undefined}
					onClick={handleAvatarClick}
					title={
						src
							? canUpload
								? "Ver avatar"
								: "Ver avatar"
							: canUpload
								? "Subir avatar"
								: "Sin avatar"
					}
					sx={{
						width: size,
						height: size,
						cursor: src || canUpload ? "pointer" : "default",
						opacity: uploading ? 0.65 : 1,
					}}
				>
					{uploading ? <CircularProgress size={Math.max(size - 10, 16)} /> : !src ? iniciales : null}
				</Avatar>

				{canUpload && !uploading && (
					<IconButton
						size="small"
						onClick={openFilePicker}
						title={hasImage ? "Actualizar avatar" : "Subir avatar"}
						sx={{
							position: "absolute",
							right: -6,
							bottom: -6,
							width: Math.max(18, Math.round(size * 0.52)),
							height: Math.max(18, Math.round(size * 0.52)),
							bgcolor: "primary.main",
							color: "common.white",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: 1,
							"&:hover": {
								bgcolor: "primary.dark",
							},
						}}
					>
						<AddAPhotoIcon sx={{ fontSize: Math.max(10, Math.round(size * 0.24)) }} />
					</IconButton>
				)}

				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					onChange={handleFileChange}
					style={{ display: "none" }}
				/>
			</Box>

			{parentDialogOpen && openPreview && (
				<Dialog open onClose={handleClosePreview} maxWidth="sm" fullWidth>
					<DialogTitle>{nombrePersona}</DialogTitle>
					<DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<Box
							component="img"
							src={src}
							alt={nombrePersona}
							sx={{
								maxWidth: "100%",
								maxHeight: "70vh",
								objectFit: "contain",
								borderRadius: 1,
							}}
						/>
					</DialogContent>
					<DialogActions>
						{canUpload && (
							<Button onClick={openFilePicker} disabled={uploading}>
								Actualizar avatar
							</Button>
						)}
						<Button onClick={handleClosePreview}>Cerrar</Button>
					</DialogActions>
				</Dialog>
			)}
		</>
	);
}
